import * as React from 'react';
import clsx from 'clsx';
import { PlayIcon, PauseIcon, ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/solid';

interface VideoMessagePlayerProps {
  url: string;
  onClose: () => void;
  onEnded?: () => void;
}

export const VideoMessagePlayer: React.FC<VideoMessagePlayerProps> = ({
  url,
  onClose,
  onEnded,
}) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const progressRef = React.useRef<HTMLDivElement>(null);
  const onEndedRef = React.useRef(onEnded);
  onEndedRef.current = onEnded;

  const [isPlaying, setIsPlaying] = React.useState(true);
  const [hasEnded, setHasEnded] = React.useState(false);
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlay = () => {
      setIsPlaying(true);
      setHasEnded(false);
    };
    const onPause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setHasEnded(true);
      onEndedRef.current?.();
    };
    const onTimeUpdate = () => {
      if (video.duration) {
        setProgress((video.currentTime / video.duration) * 100);
      }
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('timeupdate', onTimeUpdate);

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('timeupdate', onTimeUpdate);
    };
  }, []);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (hasEnded) {
      video.currentTime = 0;
      void video.play();
      return;
    }

    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    const bar = progressRef.current;
    if (!video || !bar || !video.duration) return;

    const rect = bar.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    video.currentTime = fraction * video.duration;
  };

  return (
    <div className='relative overflow-hidden rounded-24'>
      <video
        ref={videoRef}
        src={url}
        autoPlay
        playsInline
        className='block max-h-[360px] w-full rounded-24 object-cover'
      />

      {/* Center overlay: play/pause/replay */}
      <button
        type='button'
        onClick={togglePlayPause}
        className='absolute inset-0 flex items-center justify-center bg-black/0 transition-colors hover:bg-black/20'
        aria-label={hasEnded ? 'Replay' : isPlaying ? 'Pause' : 'Play'}
      >
        <span
          className={clsx(
            'flex size-12 items-center justify-center rounded-full bg-black/50 text-white transition-opacity',
            isPlaying && !hasEnded ? 'opacity-0 hover:opacity-100' : 'opacity-100'
          )}
        >
          {hasEnded ? (
            <ArrowPathIcon className='size-6' />
          ) : isPlaying ? (
            <PauseIcon className='size-6' />
          ) : (
            <PlayIcon className='ml-0.5 size-6' />
          )}
        </span>
      </button>

      {/* Close button */}
      <button
        type='button'
        onClick={onClose}
        className='absolute right-2 top-2 flex size-7 items-center justify-center rounded-full bg-black/50 text-white transition-opacity hover:bg-black/70'
        aria-label='Close video'
      >
        <XMarkIcon className='size-4' />
      </button>

      {/* Progress bar */}
      <div
        ref={progressRef}
        onClick={handleProgressClick}
        className='absolute inset-x-0 bottom-0 h-1 cursor-pointer bg-white/20'
      >
        <div
          className='h-full bg-accent-300 transition-[width] duration-100'
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};
